import type { Invite, User } from '@gmn-portal/backend';
import type { SortingState } from '@tanstack/react-table';
import { PlusCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  payloadClient,
  useCreateInvite,
  useDeleteInvite,
  useInvites,
  useUpdateInvite,
  useUpdateUser,
  useUsers,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageHeading } from '@/shared/components/layout';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui';
import { useTablePreferences } from '@/shared/services/table-preferences';

import { UserForm, type UserFormValues } from '../components/user-form';
import { UserTable } from '../components/user-table';

interface PageProps {
  allowedRoles: ('customer-admin' | 'gmn-admin')[];
  customerId?: string;
  hideInviteButton?: boolean;
  title?: string;
  onUsageChange?: (usage: { userCount: number; inviteCount: number }) => void;
}

export function UserManagement({
  allowedRoles,
  customerId,
  hideInviteButton,
  title,
  onUsageChange,
}: PageProps) {
  const { t } = useIntl();
  const { user: currentUser, updateUser: updateAuthUser } = useAuth();
  const [page] = useState(1);

  const resolvedCustomerId = customerId
    ? customerId
    : currentUser?.customer
      ? typeof currentUser.customer === 'string'
        ? currentUser.customer
        : currentUser.customer.id
      : undefined;

  const tablePreferences = useTablePreferences({
    tableId: 'user-management',
    defaultPreferences: {
      sorting: [],
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      sorting,
    });
  }, [sorting, tablePreferences]);

  const { data: usersData, isLoading: isLoadingUsers } = useUsers({
    page,
    limit: 0,
    sort: 'createdAt',
    where: {
      role: {
        in: allowedRoles,
      },
      ...(resolvedCustomerId
        ? { customer: { equals: resolvedCustomerId } }
        : { customer: { exists: false } }),
    },
  });

  const { data: invitesData, isLoading: isLoadingInvites } = useInvites({
    page,
    limit: 0,
    sort: 'createdAt',
    where: {
      ...(resolvedCustomerId
        ? { customer: { equals: resolvedCustomerId } }
        : { customer: { exists: false } }),
    },
  });

  const userCount = usersData?.totalDocs ?? 0;
  const inviteCount = invitesData?.totalDocs ?? 0;

  useEffect(() => {
    if (!resolvedCustomerId || !onUsageChange) {
      return;
    }
    onUsageChange({ userCount, inviteCount });
  }, [resolvedCustomerId, onUsageChange, userCount, inviteCount]);

  const [selectedUser, setSelectedUser] = useState<
    User | (Invite & { isInvite: true }) | undefined
  >(undefined);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const updateUser = useUpdateUser();
  const updateInvite = useUpdateInvite();
  const createInvite = useCreateInvite();
  const deleteInvite = useDeleteInvite();

  const handleEditUser = (user: User | (Invite & { isInvite: true })) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedUser(undefined);

    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 200);
  };

  const handleSubmit = async (data: UserFormValues) => {
    if (!allowedRoles.includes(data.role)) {
      toast.error(t('errors.invalidRole'));
      return;
    }

    if (selectedUser) {
      if ('isInvite' in selectedUser && selectedUser.isInvite) {
        await updateInvite.mutateAsync({
          id: selectedUser.id,
          data: {
            ...data,
            email: undefined,
            customer: resolvedCustomerId,
          },
        });
        toast.success(t('users.userUpdatedSuccessfully'));
      } else {
        const updatedUser = await updateUser.mutateAsync({
          id: selectedUser.id,
          data: {
            ...data,
            language: undefined,
            email: undefined,
            customer: resolvedCustomerId,
          },
        });

        // If the updated user is the current user, update the auth context
        if (selectedUser.id === currentUser?.id) {
          updateAuthUser(updatedUser);
        }

        toast.success(t('users.userUpdatedSuccessfully'));
      }

      handleCloseEditDialog();
    } else {
      await createInvite.mutateAsync({
        ...data,
        customer: resolvedCustomerId,
      });
      setIsInviteDialogOpen(false);
      toast.success(t('users.invitationSentSuccessfully'));
    }
  };

  const handleDelete = async (user: User | (Invite & { isInvite: true })) => {
    if ('isInvite' in user && user.isInvite) {
      await deleteInvite.mutateAsync(user.id);
      toast.success(t('users.invitationDeletedSuccessfully'));
    }
  };

  const handleResend = async (invite: User | (Invite & { isInvite: true })) => {
    const toastId = toast.loading(t('users.resendingInvitation'));
    try {
      await payloadClient.customRequest({
        subpath: '/invites/resend-invite',
        method: 'POST',
        data: { inviteId: invite.id },
      });
      toast.success(t('users.invitationResentSuccessfully'), { id: toastId });
    } catch (_error) {
      toast.error(t('errors.generalError'), { id: toastId });
    }
  };

  const isLoading = isLoadingUsers || isLoadingInvites;

  const combinedData = [
    ...(usersData?.docs ?? []),
    ...(invitesData?.docs?.map((invite: Invite) => ({
      ...invite,
      isInvite: true,
    })) ?? []),
  ];

  const inviteButton = (
    <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusCircle className="size-4 mr-2" />
          <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
            {t('users.inviteUser')}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.inviteUser')}</DialogTitle>
          <DialogDescription>{t('users.inviteUserDescription')}</DialogDescription>
        </DialogHeader>
        <UserForm onSubmit={handleSubmit} allowedRoles={allowedRoles} />
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeading>{title || t('users.users')}</PageHeading>
        {!hideInviteButton && inviteButton}
      </div>

      <UserTable
        users={combinedData}
        onEdit={handleEditUser}
        onDelete={handleDelete}
        onResend={handleResend}
        isLoading={isLoading}
        sorting={sorting}
        onSortingChange={setSorting}
      />

      <Dialog open={isEditDialogOpen} onOpenChange={handleCloseEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.editUser')}</DialogTitle>
            <DialogDescription>{t('users.editUserDescription')}</DialogDescription>
          </DialogHeader>
          <UserForm user={selectedUser} onSubmit={handleSubmit} allowedRoles={allowedRoles} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
